/**
 * The schema of every record one record's references point at, read straight
 * out of the cache.
 *
 * A card may group by, or ask about, a field on the row a reference points at —
 * "by the customer's area". The key it comes back with is an id on the OTHER
 * record, and only that record's schema holds the word for it. Without this a
 * bar chart would be labelled with option ids, which is a card drawn in a
 * language nobody reads.
 *
 * Synchronous, because a card is presented during a render pass, and the sync
 * engine has already written every schema it pulled into `schema_cache`. A
 * record this device has not pulled is simply absent, and the presenter then
 * falls back to the plainest true words it can say.
 *
 * The async sibling is `components/formula/use-hop-targets.ts`, which the
 * builder screens use because they may need a schema the mirror has not got.
 * This one is the drawing path, where waiting is not an option.
 */
import type { RecordSchemaResponse } from '@alluzo/shared';

import { schemaFor } from '@/lib/entry-repo';
import type { FormulaWords } from '@/lib/formula-draft';
import type { LeafWords } from '@/lib/insight-draft';
import type { HopSchemas } from '@/lib/insight-present';

export const hopSchemasOf = (schema: RecordSchemaResponse | null): HopSchemas => {
  const found = new Map<string, RecordSchemaResponse>();
  if (schema === null) return found;
  for (const field of schema.fields) {
    if (field.type !== 'reference' || field.targetRecordId === null) continue;
    if (found.has(field.targetRecordId)) continue;
    const held = schemaFor(field.targetRecordId);
    if (held !== null) found.set(field.targetRecordId, held);
  }
  return found;
};

/**
 * What it takes to say one total of a combine in words.
 *
 * A total may count another record, so naming it needs that record's own schema
 * and the schemas ITS references reach — none of which the screen holding the
 * card has. Both come from the cache, synchronously, for the reason above.
 *
 * @param names the catalogue's own word for each record, which is the tenant's
 *              and not ours to guess. A record missing from it falls back to
 *              the cached schema's name, and then to its id.
 */
export const leafWordsOf = (
  host: string,
  schema: RecordSchemaResponse | null,
  targets: HopSchemas,
  names: Readonly<Record<string, string>>,
): LeafWords => {
  // One read per record, however many totals name it. The cache lives as long
  // as this object does — one render — so a schema that changes underneath is
  // picked up on the next one.
  const found = new Map<string, FormulaWords | null>();
  const wordsOf = (recordId: string): FormulaWords | null => {
    if (recordId === host) return schema === null ? null : { source: schema, targets };
    if (found.has(recordId)) return found.get(recordId) ?? null;
    const held = schemaFor(recordId);
    const words = held === null ? null : { source: held, targets: hopSchemasOf(held) };
    found.set(recordId, words);
    return words;
  };
  return {
    host,
    nameOf: (recordId) => names[recordId] ?? wordsOf(recordId)?.source.name ?? recordId,
    wordsOf,
  };
};
